'use client';
import React from 'react';
import Link from 'next/link';
import Box from '@mui/material/Box';
import { styled } from '@mui/material';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import { handleTrackingEventName } from '../../utils/handleTrackingEventName';
import { localizeHref } from '../../utils/localizeHref';

export interface SearchResultItemProps {
  hit?: HitProps;
  components?: any;
}

interface CategoryProps {
  name: string;
  href: string;
}

export interface HitProps {
  locale: string;
  title?: string;
  path: string;
  summary?: string;
  categories?: string[];
  categoryLinks: Array<CategoryProps>;
  /** Review state, indexed so search can show it without opening the article. */
  recertificationStatus?: string;
  kmoDate?: string;
}

/**
 * The review-overdue marker is not rendered.
 *
 * It was built against an acceptance criterion we wrote, not a request from
 * IAS, and Nicole cut it on the 2026-09-02 call: fine on the Resource Center,
 * not on the Help Center, because their own customers read that page.
 *
 * recertificationStatus and kmoDate are still resolved and indexed by
 * graphql-extensions/src/Article.ts. They cost nothing to carry, they are what
 * a per-site version of this would need, and removing them would make revisiting
 * this a reindex rather than a component change.
 */

export const SearchResultItem = ({ hit, components }: SearchResultItemProps) => {
  const localizedPath = localizeHref(hit?.path, hit?.locale);
  return (
    <Root p={2} data-testid="SearchResultItem">
      <Link href={localizedPath as any} style={{ textDecoration: 'none' }} onClick={() =>
        handleTrackingEventName({ trackingEventName: 'Algolia Search Result Click', href: localizedPath })
      }>

        <Box>
          {hit?.title ? (
            <Typography variant="body1" data-testid="SearchResultItem-title">
              <strong>
                <components.Highlight hit={hit} attribute="title" />
              </strong>
            </Typography>
          ) : (
            <Skeleton animation="wave" height={30} width="40%" />
          )}
          {!!hit?.summary && (
            <Typography variant="body1" sx={{ py: [2] }} data-testid="SearchResultItem-summary">
              <components.Highlight hit={hit} attribute="summary" />
            </Typography>
          )}
          {hit?.categoryLinks?.map((category, idx) => {
            const localizedCategoryHref = localizeHref(category?.href, hit?.locale);
            return (
              <Link href={localizedCategoryHref}>
                <Chip
                  key={localizedCategoryHref}
                  clickable
                  component="a"
                  sx={{
                    marginRight: 1,
                    marginTop: 1,
                    borderRadius: '3px',
                    backgroundColor: 'coolGrey.light',
                    fontWeight: 'bold',
                    color: 'grey.100'
                  }}
                  // TODO: Not working but will replace this library very soon
                  // label={<components.Highlight hit={hit} attribute={['categories', 'level-1']} />}
                  label={category.name}
                  data-testid={`SearchResultItem-category${idx}`}
                />
              </Link>
            );
          })}
          <br />
        </Box>

      </Link>
    </Root>
  );
};

const Root = styled(Box, {
  name: 'SearchResultItem',
  slot: 'Root',
  overridesResolver: (_, styles) => [styles.root]
})<{ variant?: string }>(({ theme }) => ({
  '& mark': {
    // TODO: currently throwing: "TypeError: Cannot read properties of undefined"
    // https://lastrev.atlassian.net/browse/IAS-249
    // backgroundColor: theme.palette.yellow.light
    backgroundColor: '#FFE173'
  },

  '& .MuiChip-root': {
    'height': 'auto',
    'padding': theme.spacing(0.5, 0),
    // TODO: currently throwing: "TypeError: Cannot read properties of undefined"
    // https://lastrev.atlassian.net/browse/IAS-249
    // backgroundColor: theme.palette.coolGrey.light,
    'backgroundColor': '#D3EBED',
    'color': theme.palette.text.primary,

    '&:hover': {
      backgroundColor: '#C3DCDE'
    }
  }
}));

export default SearchResultItem;
